import { useState, useEffect, useMemo } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { HelpPanel } from '../components/ui/help-panel';
import { 
  Search,
  Filter,
  Eye,
  History,
  CalendarIcon,
  X,
  ChevronUp,
  ChevronDown,
  User,
  FileText,
  Clock,
  Globe,
  ArrowRight,
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';
import { format, formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Action color mapping
const actionColors = {
  create: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  update: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  delete: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400',
  post: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400',
  void: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  approve: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  reject: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400',
  reverse: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  sync: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  login: 'bg-muted text-foreground dark:bg-gray-700/50 dark:text-gray-200',
  record_payment: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
};

// Action icons
const actionIcons = {
  create: Plus,
  update: Pencil,
  delete: Trash2,
  post: CheckCircle2,
  void: XCircle,
  approve: CheckCircle2,
  reject: XCircle,
  reverse: RefreshCw,
  sync: RefreshCw,
  login: User,
  record_payment: CheckCircle2,
};

// Help content
const auditHelp = {
  title: 'Audit Log',
  description: 'Track all changes made to your workspace',
  sections: [
    {
      heading: 'What is tracked?',
      content: 'Every create, update, delete, post, void, and approval action is logged with timestamp, user, and details.'
    },
    {
      heading: 'Action Types',
      type: 'info',
      content: (
        <ul className="space-y-1 mt-2 text-sm">
          <li><strong>Create</strong> - New record created</li>
          <li><strong>Update</strong> - Record modified</li>
          <li><strong>Delete</strong> - Record removed</li>
          <li><strong>Post</strong> - Draft finalized</li>
          <li><strong>Void</strong> - Record cancelled</li>
        </ul>
      )
    }
  ],
  tips: [
    'Use filters to narrow down the audit trail',
    'Click "View" to see full details of any event',
    'Export audit logs for compliance documentation'
  ]
};

// Format timestamp
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return format(date, 'MMM d, yyyy h:mm a');
};

// Event Detail Modal
const EventDetailModal = ({ event, open, onClose }) => {
  if (!event) return null;
  
  const ActionIcon = actionIcons[event.action] || FileText;
  const changes = event.details?.changes || null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Audit Event Details
          </DialogTitle>
          <DialogDescription>
            Event ID: {event.audit_id}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Event Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Timestamp</p>
              <p className="font-medium">{formatTimestamp(event.created_at)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">User</p>
              <div className="flex items-center gap-2">
                <Avatar className="w-6 h-6">
                  <AvatarFallback className="text-xs bg-gradient-to-br from-emerald-500 to-blue-500 text-white">
                    {event.actor_name?.charAt(0)?.toUpperCase() || 'S'}
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium">{event.actor_name || 'System'}</span>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Email</p>
              <p className="text-sm">{event.actor_email || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">IP Address</p>
              <p className="font-mono text-sm">{event.ip_address || '-'}</p>
            </div>
          </div>
          
          <Separator />
          
          {/* Action Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Action</p>
              <Badge className={cn("font-medium", actionColors[event.action])}>
                <ActionIcon className="w-3 h-3 mr-1" />
                {event.action.charAt(0).toUpperCase() + event.action.slice(1).replace(/_/g, ' ')}
              </Badge>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Resource</p>
              <div className="flex items-center gap-2">
                <Badge variant="outline">
                  {event.resource.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
                <span className="font-mono text-xs text-muted-foreground">
                  {event.resource_id.substring(0, 8)}...
                </span>
              </div>
            </div>
          </div>
          
          {/* Description */}
          {event.description && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Description</p>
              <p className="text-sm">{event.description}</p>
            </div>
          )}
          
          {/* Changes (Before/After) */}
          {changes && (
            <>
              <Separator />
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Changes</p>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Before */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-rose-600 dark:text-rose-400">Before</p>
                    <ScrollArea className="h-32 rounded-lg border bg-muted/30 p-3">
                      <pre className="text-xs font-mono whitespace-pre-wrap">
                        {JSON.stringify(changes.before || {}, null, 2)}
                      </pre>
                    </ScrollArea>
                  </div>
                  {/* After */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">After</p>
                    <ScrollArea className="h-32 rounded-lg border bg-muted/30 p-3">
                      <pre className="text-xs font-mono whitespace-pre-wrap">
                        {JSON.stringify(changes.after || {}, null, 2)}
                      </pre>
                    </ScrollArea>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Raw Details */}
          {event.details && Object.keys(event.details).length > 0 && !changes && (
            <>
              <Separator />
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Additional Details</p>
                <ScrollArea className="h-32 rounded-lg border bg-muted/30 p-3">
                  <pre className="text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(event.details, null, 2)}
                  </pre>
                </ScrollArea>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function AuditLog() {
  const { currentWorkspace } = useWorkspace();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Unique users for filter dropdown
  const uniqueUsers = useMemo(() => {
    const users = new Map();
    events.forEach(e => {
      if (e.actor_id && e.actor_name) {
        users.set(e.actor_id, e.actor_name);
      }
    });
    return Array.from(users.entries()).map(([id, name]) => ({ id, name }));
  }, [events]);

  // Unique resources for filter dropdown
  const uniqueResources = useMemo(() => {
    return [...new Set(events.map(e => e.resource))].sort();
  }, [events]);

  // Unique actions for filter dropdown
  const uniqueActions = useMemo(() => {
    return [...new Set(events.map(e => e.action))].sort();
  }, [events]);

  useEffect(() => {
    const fetchAuditLog = async () => {
      if (!currentWorkspace) return;
      
      setLoading(true);
      try {
        let url = `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/audit?limit=500`;
        
        if (resourceFilter !== 'all') {
          url += `&resource=${resourceFilter}`;
        }
        if (actionFilter !== 'all') {
          url += `&action=${actionFilter}`;
        }

        const response = await fetch(url);
        
        if (response.ok) {
          const data = await response.json();
          setEvents(data);
        }
      } catch (error) {
        console.error('Failed to fetch audit log:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLog();
  }, [currentWorkspace, resourceFilter, actionFilter]);

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    let result = events.filter(event => {
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesSearch = 
          event.actor_name?.toLowerCase().includes(searchLower) ||
          event.resource.toLowerCase().includes(searchLower) ||
          event.resource_id.toLowerCase().includes(searchLower) ||
          event.description?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      
      // User filter
      if (userFilter !== 'all' && event.actor_id !== userFilter) return false;
      
      // Date filter
      if (startDate && new Date(event.created_at) < startDate) return false;
      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (new Date(event.created_at) > endOfDay) return false;
      }
      
      return true;
    });
    
    // Sort
    result.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      if (sortConfig.key === 'created_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [events, search, userFilter, startDate, endDate, sortConfig]);

  // Pagination
  const paginatedEvents = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredEvents.slice(start, start + pageSize);
  }, [filteredEvents, page]);

  const totalPages = Math.ceil(filteredEvents.length / pageSize);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const clearFilters = () => {
    setSearch('');
    setResourceFilter('all');
    setActionFilter('all');
    setUserFilter('all');
    setStartDate(null);
    setEndDate(null);
  };

  const hasFilters = search || resourceFilter !== 'all' || actionFilter !== 'all' || userFilter !== 'all' || startDate || endDate;

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="w-4 h-4" /> : 
      <ChevronDown className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Audit Log</h1>
          <p className="text-muted-foreground">
            Track all changes made to your workspace
          </p>
        </div>
        <HelpPanel {...auditHelp} />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search events..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="audit-search"
              />
            </div>
            
            {/* Action Filter */}
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[140px]" data-testid="action-filter">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {uniqueActions.map(action => (
                  <SelectItem key={action} value={action}>
                    {action.charAt(0).toUpperCase() + action.slice(1).replace(/_/g, ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Resource Filter */}
            <Select value={resourceFilter} onValueChange={setResourceFilter}>
              <SelectTrigger className="w-[160px]" data-testid="resource-filter">
                <SelectValue placeholder="Resource" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                {uniqueResources.map(resource => (
                  <SelectItem key={resource} value={resource}>
                    {resource.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* User Filter */}
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-[160px]" data-testid="user-filter">
                <SelectValue placeholder="User" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {uniqueUsers.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date Range */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {startDate ? format(startDate, 'MMM d') : 'Start'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {endDate ? format(endDate, 'MMM d') : 'End'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                />
              </PopoverContent>
            </Popover>

            {/* Clear Filters */}
            {hasFilters && (
              <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters">
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
          
          {/* Results count */}
          <p className="text-sm text-muted-foreground mt-3">
            Showing {paginatedEvents.length} of {filteredEvents.length} events
          </p>
        </CardContent>
      </Card>

      {/* Events Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Timestamp
                    <SortIcon column="created_at" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('actor_name')}
                >
                  <div className="flex items-center gap-1">
                    User
                    <SortIcon column="actor_name" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('action')}
                >
                  <div className="flex items-center gap-1">
                    Action
                    <SortIcon column="action" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('resource')}
                >
                  <div className="flex items-center gap-1">
                    Resource
                    <SortIcon column="resource" />
                  </div>
                </TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedEvents.length > 0 ? (
                paginatedEvents.map((event) => {
                  const ActionIcon = actionIcons[event.action] || FileText;
                  return (
                    <TableRow key={event.audit_id} data-testid={`audit-row-${event.audit_id}`}>
                      <TableCell>
                        <div className="space-y-0.5">
                          <p className="font-mono text-sm">
                            {format(new Date(event.created_at), 'MMM d, yyyy')}
                          </p>
                          <p className="text-xs text-muted-foreground font-mono">
                            {format(new Date(event.created_at), 'h:mm:ss a')}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="w-7 h-7">
                            <AvatarFallback className="text-xs bg-gradient-to-br from-emerald-500 to-blue-500 text-white">
                              {event.actor_name?.charAt(0)?.toUpperCase() || 'S'}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium truncate max-w-[120px]">
                            {event.actor_name || 'System'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn("font-medium gap-1", actionColors[event.action] || actionColors.update)}>
                          <ActionIcon className="w-3 h-3" />
                          {event.action.charAt(0).toUpperCase() + event.action.slice(1).replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-normal">
                            {event.resource.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Badge>
                          <span className="font-mono text-xs text-muted-foreground">
                            {event.resource_id.substring(0, 8)}...
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                          {event.description || '-'}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedEvent(event)}
                          data-testid={`view-event-${event.audit_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <History className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No audit events found</p>
                    {hasFilters && (
                      <Button variant="link" onClick={clearFilters} className="mt-2">
                        Clear filters
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Event Detail Modal */}
      <EventDetailModal 
        event={selectedEvent} 
        open={!!selectedEvent} 
        onClose={() => setSelectedEvent(null)} 
      />
    </div>
  );
}
